import React, {FC, useState} from "react";
import moment from "moment";
import {Alert, Button, Checkbox, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message} from "antd";
import {
    DunningLevel,
    GetBookingDunningsQuery,
    useCreateDunningMutation,
    useGetBookingDunningsQuery,
    useGetCompanyInfoQuery,
    useGetDunningPdfLazyQuery,
    useGetDunningProposalQuery,
    useResendDunningMutation,
    useSetDunningPauseMutation,
    useWithdrawDunningMutation,
} from "../../../../generated/graphql";
import i18n from "../../../../services/i18n";
import PanelCard from "../../../../views/PanelCard/PanelCard";
import {renderAmount} from "../../../../helpers/renderAmount";
import PDFViewerModal from "../../../../views/PDFViewerModal/PDFViewerModal";
import {resolveServerErrorMessage} from "../../../../helpers/resolveServerErrorMessage";
import {useHasRoleAtLeast} from "../../../../helpers/useIsStaffOnlyAccess";
import Roles from "../../../../../shared/models/roles";
import DateInput from "../../../../views/DateInput/DateInput";

type Dunning = GetBookingDunningsQuery["dunningsByBooking"][number];

const LEVEL_ORDER = [DunningLevel.Reminder, DunningLevel.Level1, DunningLevel.Level2, DunningLevel.Level3];
// Getter statt Modul-Konstante: ein i18n-Proxy als Modul-Binding bringt react-hot-loader im Dev-Modus zum Absturz
const tr = () => i18n.containers.invoice.BookingDunnings;
const levelLabel = (level: DunningLevel) => tr().levels[level]();
const date = (value?: string | Date | null) => (value ? moment(value).format("DD.MM.YYYY") : "–");
const money = (amount: number) => renderAmount({amount});
const isActive = (d: Dunning) => !d.withdrawnAt && !d.voidedAt;
// Puppeteer-Meldungen, wenn das PDF nicht erzeugt werden konnte (Browser startet nicht, Zeitüberschreitung)
const PDF_RENDER_ERROR = /waiting for target failed|Timed out after|Target closed|Navigation timeout/i;

type Props = {
    bookingId: string;
    /** Ohne Rechnung gibt es nichts zu mahnen; der Abschnitt bleibt dann weg */
    hasInvoice: boolean;
    /** Gebühren ändern die Raten der Buchung */
    refetchBooking: () => void;
    /** Rechnung storniert: nichts mehr zu mahnen, die Mahnsperre hat keinen Zweck mehr */
    isCanceled?: boolean;
};

/** Mahnwesen (#1517): Liste, nächste Stufe, Mahnsperre. Nur mit Flag featureDunning und ab Rolle Admin. */
const BookingDunningsPanel: FC<Props> = ({bookingId, hasInvoice, refetchBooking, isCanceled}) => {
    const {data: company} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const isAdmin = useHasRoleAtLeast(Roles.Admin);
    const enabled = Boolean(company?.companyInfo?.subscription?.featureDunning) && isAdmin && hasInvoice;

    if (!enabled) {
        return null;
    }

    return <DunningsCard bookingId={bookingId} refetchBooking={refetchBooking} isCanceled={isCanceled} />;
};

const DunningsCard: FC<Omit<Props, "hasInvoice">> = ({bookingId, refetchBooking, isCanceled}) => {
    const {data, refetch} = useGetBookingDunningsQuery({variables: {bookingId}, fetchPolicy: "network-only"});
    const {data: proposalData, refetch: refetchProposal} = useGetDunningProposalQuery({variables: {bookingId}, fetchPolicy: "network-only"});
    const [loadPdf] = useGetDunningPdfLazyQuery({fetchPolicy: "network-only"});
    // PDF wie die Rechnung im Vorschau-Modal statt als Download (Befund 24, 30.10.2026)
    const [pdfPreview, setPdfPreview] = useState<{body: string; fileName: string; level: DunningLevel}>();
    const [resend] = useResendDunningMutation();
    const [createOpen, setCreateOpen] = useState(false);
    const [pauseOpen, setPauseOpen] = useState(false);
    const [withdrawing, setWithdrawing] = useState<Dunning | undefined>();

    const dunnings = data?.dunningsByBooking ?? [];
    const pause = data?.dunningPause;
    const proposal = proposalData?.dunningProposal;
    const active = dunnings.filter((d) => isActive(d) && d.completedAt);
    const current = active.reduce<DunningLevel | undefined>((max, d) => (max === undefined || LEVEL_ORDER.indexOf(d.level) > LEVEL_ORDER.indexOf(max) ? d.level : max), undefined);
    const pauseActive = Boolean(pause?.until && moment(pause.until).isSameOrAfter(moment(), "day"));

    const reload = async () => {
        await Promise.all([refetch(), refetchProposal()]);
        refetchBooking();
    };

    const downloadPdf = async (d: Dunning) => {
        try {
            const result = await loadPdf({variables: {id: d.id}});

            if (result.error) {
                throw result.error;
            }
            setPdfPreview({body: result.data?.dunningPdf ?? "", fileName: `${d.dunningNumber}.pdf`, level: d.level});
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
        }
    };

    // Läuft in der Karte, die nach dem Schließen des Dialogs gemountet bleibt
    const downloadCreated = async (created: CreatedDunning) => {
        const pdf = await loadPdf({variables: {id: created.id}}).catch(() => undefined);

        if (pdf?.data?.dunningPdf) {
            setPdfPreview({body: pdf.data.dunningPdf, fileName: `${created.dunningNumber}.pdf`, level: created.level});
        } else {
            message.warning(tr().dialog.pdfFailed({number: created.dunningNumber}));
        }
    };

    const resendMail = async (d: Dunning) => {
        try {
            await resend({variables: {id: d.id}});
            message.success(tr().actions.resent());
            await reload();
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
        }
    };

    const statusTag = (d: Dunning) => {
        if (d.voidedAt) {
            return <Tag>{tr().status.voided()}</Tag>;
        }
        if (d.withdrawnAt) {
            return (
                <Space direction="vertical" size={0}>
                    <Tag color="red">{tr().status.withdrawn()}</Tag>
                    {d.withdrawnReason && <Typography.Text type="secondary">{d.withdrawnReason}</Typography.Text>}
                </Space>
            );
        }
        if (!d.completedAt) {
            return <Tag>{tr().status.pending()}</Tag>;
        }
        if (d.sentAt) {
            const sentDate = moment(d.sentAt).format("DD.MM. HH:mm");

            return <Tag color="green">{tr().status.sent({date: sentDate})}</Tag>;
        }

        return <Tag color="orange">{tr().status.notSent()}</Tag>;
    };

    const columns = [
        {title: tr().columns.level(), key: "level", render: (_: unknown, d: Dunning) => levelLabel(d.level)},
        {title: tr().columns.number(), dataIndex: "dunningNumber", key: "number"},
        {title: tr().columns.date(), key: "date", render: (_: unknown, d: Dunning) => date(d.createDate)},
        {title: tr().columns.open(), key: "open", align: "right" as const, render: (_: unknown, d: Dunning) => money(d.openAmount)},
        {title: tr().columns.fee(), key: "fee", align: "right" as const, render: (_: unknown, d: Dunning) => (d.feeAmount > 0 ? money(d.feeAmount) : "–")},
        {title: tr().columns.total(), key: "total", align: "right" as const, render: (_: unknown, d: Dunning) => money(d.totalAmount)},
        {title: tr().columns.due(), key: "due", render: (_: unknown, d: Dunning) => date(d.paymentDue)},
        {title: tr().columns.sent(), key: "sent", render: (_: unknown, d: Dunning) => statusTag(d)},
        {
            title: "",
            key: "actions",
            render: (_: unknown, d: Dunning) =>
                d.completedAt ? (
                    <Space size={4} wrap>
                        <Button type="link" size="small" onClick={async () => downloadPdf(d)}>
                            {tr().actions.pdf()}
                        </Button>
                        {isActive(d) && d.feeDeleted && (
                            // Das PDF nennt noch die gelöschte Gebühr
                            <Tooltip title={tr().actions.resendFeeDeleted()}>
                                <Button type="link" size="small" disabled>
                                    {tr().actions.resend()}
                                </Button>
                            </Tooltip>
                        )}
                        {isActive(d) && !d.feeDeleted && (
                            <Popconfirm title={tr().actions.resendConfirm()} onConfirm={async () => resendMail(d)}>
                                <Button type="link" size="small">
                                    {tr().actions.resend()}
                                </Button>
                            </Popconfirm>
                        )}
                        {isActive(d) && d.level === current && (
                            <Button type="link" size="small" danger onClick={() => setWithdrawing(d)}>
                                {tr().actions.withdraw()}
                            </Button>
                        )}
                    </Space>
                ) : null,
        },
    ];

    const nextLabel = proposal?.level ? levelLabel(proposal.level) : undefined;
    const createLabel = nextLabel ? tr().create({level: nextLabel}) : undefined;
    const title = (
        <Space>
            <span>{tr().title()}</span>
            {current && <Tag color="orange">{tr().current({level: levelLabel(current)})}</Tag>}
        </Space>
    );
    const extra = (
        <Space>
            {/* size small: die Knöpfe sollen in die Überschriftenzeile des Panels passen (Befund 4, 30.10.2026) */}
            {!isCanceled && (
                <Button size="small" onClick={() => setPauseOpen(true)}>
                    {pause?.until ? tr().pause.edit() : tr().pause.set()}
                </Button>
            )}
            {createLabel && (
                <Tooltip title={proposal?.blocker}>
                    <Button size="small" type="primary" disabled={Boolean(proposal?.blocker)} onClick={() => setCreateOpen(true)}>
                        {createLabel}
                    </Button>
                </Tooltip>
            )}
        </Space>
    );
    const pauseUntil = date(pause?.until);
    const activeDunning = data?.activeDunning;
    // Gemahnter und heutiger offener Betrag derselben Posten weichen ab (Zahlung, Raten geändert)
    const amountChanged = activeDunning && Math.abs(activeDunning.currentOpenAmount - activeDunning.openAmount) >= 0.005;

    return (
        <PanelCard title={title} columns={1} gapAfter extra={extra}>
            {pauseActive && <Alert type="info" showIcon style={{marginBottom: 12}} message={tr().pause.active({date: pauseUntil})} description={pause?.note} />}
            {!pauseActive && proposal && !proposal.blocker && nextLabel && (
                <Alert
                    type="warning"
                    showIcon
                    style={{marginBottom: 12}}
                    message={proposal.tooEarly ? tr().due.tooEarlyTitle({level: nextLabel}) : tr().due.title({level: nextLabel})}
                    description={proposal.tooEarly ? tr().due.tooEarlyText({date: date(proposal.earliestDate)}) : tr().due.text({level: nextLabel})}
                />
            )}
            {!pauseActive && proposal?.blocker && (
                <Typography.Paragraph type="secondary" style={{marginBottom: 12}}>
                    {proposal.blocker}
                </Typography.Paragraph>
            )}
            {amountChanged && activeDunning && (
                <Typography.Paragraph type="warning" style={{marginBottom: 12}}>
                    {tr().hint.changed({level: levelLabel(activeDunning.level), before: money(activeDunning.openAmount), now: money(activeDunning.currentOpenAmount)})}
                </Typography.Paragraph>
            )}
            {dunnings.length > 0 && <Table rowKey="id" size="small" pagination={false} dataSource={dunnings} columns={columns} />}
            {createOpen && proposal?.level && <CreateDunningModal bookingId={bookingId} suggestedLevel={proposal.level} onClose={() => setCreateOpen(false)} onDone={reload} onCreatedPdfOnly={downloadCreated} />}
            {pauseOpen && <PauseModal bookingId={bookingId} until={pause?.until ?? undefined} note={pause?.note ?? undefined} onClose={() => setPauseOpen(false)} onDone={reload} />}
            {withdrawing && <WithdrawModal dunning={withdrawing} onClose={() => setWithdrawing(undefined)} onDone={reload} />}
            {pdfPreview && (
                <PDFViewerModal
                    open
                    title={pdfPreview.fileName}
                    invoiceBody={pdfPreview.body}
                    invoiceFileName={pdfPreview.fileName}
                    kindOfDocument={levelLabel(pdfPreview.level)}
                    handleOk={() => setPdfPreview(undefined)}
                    handleCancel={() => setPdfPreview(undefined)}
                />
            )}
        </PanelCard>
    );
};

type CreatedDunning = {id: string; dunningNumber: string; level: DunningLevel};

const CreateDunningModal: FC<{bookingId: string; suggestedLevel: DunningLevel; onClose: () => void; onDone: () => Promise<void>; onCreatedPdfOnly: (created: CreatedDunning) => Promise<void>}> = ({
    bookingId,
    suggestedLevel,
    onClose,
    onDone,
    onCreatedPdfOnly,
}) => {
    const [level, setLevel] = useState<DunningLevel>(suggestedLevel);
    const [force, setForce] = useState(false);
    // undefined = Gebühr laut Einstellungen übernehmen
    const [fee, setFee] = useState<number | undefined>();
    // Fehler bleibt im Dialog stehen: nach bis zu 30 s Warten auf das PDF übersieht man eine kurze Einblendung
    const [submitError, setSubmitError] = useState<string>();
    const {data, loading} = useGetDunningProposalQuery({variables: {bookingId, level}, fetchPolicy: "network-only"});
    const [create, {loading: creating}] = useCreateDunningMutation();
    const proposal = data?.dunningProposal;
    const canSubmit = Boolean(proposal && !proposal.blocker && (!proposal.tooEarly || force));
    const canHaveFee = level !== DunningLevel.Reminder;
    const feeAmount = fee ?? proposal?.feeAmount ?? 0;

    const submit = async (sendMail: boolean) => {
        let result: Awaited<ReturnType<typeof create>>;

        setSubmitError(undefined);
        try {
            result = await create({variables: {input: {bookingId, level, sendMail, force: force || undefined, feeAmount: canHaveFee ? fee : undefined}}});
        } catch (error) {
            const text = resolveServerErrorMessage(error, tr().failed());

            // Abbruch der PDF-Erzeugung (Puppeteer) in verständliche Worte; die Mahnung wurde dann nicht angelegt
            setSubmitError(PDF_RENDER_ERROR.test(text) ? tr().dialog.pdfRenderFailed() : text);

            return;
        }
        const created = result.data?.createDunning;

        if (created && sendMail && !created.sentAt) {
            message.warning(tr().dialog.mailFailed({number: created.dunningNumber}));
        } else if (created) {
            message.success(tr().dialog.created({number: created.dunningNumber}));
        }
        // Die Mahnung steht ab hier: Dialog sofort schließen und neu laden; den Download übernimmt die Karte (Codex #1534, #1539)
        onClose();
        if (created && !sendMail) {
            void onCreatedPdfOnly({...created, level});
        }
        await onDone();
    };

    const row = (label: string, content: React.ReactNode) => (
        <div style={{display: "flex", gap: 16, marginBottom: 12}}>
            <div style={{width: 120, flexShrink: 0}}>{label}</div>
            <div style={{flex: 1}}>{content}</div>
        </div>
    );
    const levelOptions = LEVEL_ORDER.map((l) => {
        const label = levelLabel(l);

        return {value: l, label: l === suggestedLevel ? tr().dialog.suggested({level: label}) : label};
    });

    return (
        <Modal
            visible
            title={tr().create({level: levelLabel(level)})}
            onCancel={onClose}
            width={640}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {tr().dialog.cancel()}
                </Button>,
                <Button key="pdf" disabled={!canSubmit} loading={creating} onClick={async () => submit(false)}>
                    {tr().dialog.pdfOnly()}
                </Button>,
                <Button key="send" type="primary" disabled={!canSubmit || !proposal?.recipientEmail} loading={creating} onClick={async () => submit(true)}>
                    {tr().dialog.createAndSend()}
                </Button>,
            ]}
        >
            {submitError && <Alert type="error" showIcon style={{marginBottom: 12}} message={submitError} data-testid="dunning-create-error" />}
            {row(
                tr().dialog.level(),
                <Select
                    style={{width: "100%"}}
                    value={level}
                    options={levelOptions}
                    loading={loading}
                    onChange={(value: DunningLevel) => {
                        setLevel(value);
                        setFee(undefined);
                    }}
                />,
            )}
            {proposal?.blocker && <Alert type="error" showIcon style={{marginBottom: 12}} message={proposal.blocker} />}
            {proposal && !proposal.blocker && (
                <>
                    {row(tr().dialog.number(), proposal.dunningNumber)}
                    {row(
                        tr().dialog.amount(),
                        <div style={{background: "#fafafa", border: "1px solid #f0f0f0", padding: 12}}>
                            {proposal.items.map((item) => (
                                <div key={item.scheduledPaymentId} style={{display: "flex", justifyContent: "space-between", gap: 16}}>
                                    <span>{tr().dialog.dueItem({title: item.title, date: date(item.paymentDue)})}</span>
                                    <span style={{whiteSpace: "nowrap"}}>{money(item.openAmount)}</span>
                                </div>
                            ))}
                            {canHaveFee && (
                                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16}}>
                                    <span>
                                        {tr().dialog.newFee()}
                                        {feeAmount !== proposal.feeAmount && <Typography.Text type="secondary">{` (${tr().dialog.feeFromSettings({amount: money(proposal.feeAmount)})})`}</Typography.Text>}
                                    </span>
                                    <InputNumber
                                        aria-label={tr().dialog.newFee()}
                                        size="small"
                                        style={{width: 100}}
                                        min={0}
                                        max={100000}
                                        step={1}
                                        precision={2}
                                        decimalSeparator=","
                                        value={feeAmount}
                                        onChange={(value) => setFee(typeof value === "number" ? value : 0)}
                                    />
                                </div>
                            )}
                            <div style={{display: "flex", justifyContent: "space-between", gap: 16, borderTop: "1px solid #d9d9d9", marginTop: 8, paddingTop: 8, fontWeight: 600}}>
                                <span>{tr().dialog.total()}</span>
                                <span style={{whiteSpace: "nowrap"}}>{money(Math.round((proposal.openAmount + (canHaveFee ? feeAmount : 0)) * 100) / 100)}</span>
                            </div>
                        </div>,
                    )}
                    {row(tr().dialog.newDue(), date(proposal.paymentDue))}
                    {row(tr().dialog.dispatch(), proposal.recipientEmail ? tr().dialog.byMail({email: proposal.recipientEmail}) : <Typography.Text type="warning">{tr().dialog.noMail()}</Typography.Text>)}
                    {proposal.tooEarly && (
                        <Alert
                            type="warning"
                            showIcon
                            message={tr().dialog.tooEarly({date: date(proposal.earliestDate)})}
                            description={
                                <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)}>
                                    {tr().dialog.force()}
                                </Checkbox>
                            }
                        />
                    )}
                </>
            )}
        </Modal>
    );
};

const WithdrawModal: FC<{dunning: Dunning; onClose: () => void; onDone: () => Promise<void>}> = ({dunning, onClose, onDone}) => {
    const [reason, setReason] = useState("");
    const [withdraw, {loading}] = useWithdrawDunningMutation();

    const submit = async () => {
        if (!reason.trim()) {
            message.error(tr().withdraw.reasonRequired());

            return;
        }
        try {
            await withdraw({variables: {id: dunning.id, reason}});
            message.success(tr().withdraw.done());
            onClose();
            await onDone();
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
        }
    };

    return (
        <Modal visible title={tr().withdraw.title({number: dunning.dunningNumber})} okText={tr().withdraw.ok()} okButtonProps={{danger: true, loading}} onOk={submit} onCancel={onClose}>
            <Typography.Paragraph>{tr().withdraw.text()}</Typography.Paragraph>
            <Input.TextArea rows={3} maxLength={500} placeholder={tr().withdraw.reason()} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Modal>
    );
};

const PauseModal: FC<{bookingId: string; until?: string; note?: string; onClose: () => void; onDone: () => Promise<void>}> = ({bookingId, until, note, onClose, onDone}) => {
    const [value, setValue] = useState<moment.Moment | null>(until ? moment(until) : null);
    const [text, setText] = useState(note ?? "");
    const [setPause, {loading}] = useSetDunningPauseMutation();

    const save = async (clear: boolean) => {
        try {
            await setPause({variables: {input: {bookingId, until: clear || !value ? undefined : value.format("YYYY-MM-DD"), note: clear ? undefined : text}}});
            message.success(clear || !value ? tr().pause.cleared() : tr().pause.saved());
            onClose();
            await onDone();
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
        }
    };

    return (
        <Modal
            visible
            title={tr().pause.modalTitle()}
            onCancel={onClose}
            footer={[
                until ? (
                    <Button key="clear" danger loading={loading} onClick={async () => save(true)}>
                        {tr().pause.clear()}
                    </Button>
                ) : null,
                <Button key="save" type="primary" loading={loading} disabled={!value} onClick={async () => save(false)}>
                    {tr().pause.save()}
                </Button>,
            ]}
        >
            <Space direction="vertical" style={{width: "100%"}}>
                <span>{tr().pause.until()}</span>
                <DateInput displayFormat="DD.MM.YYYY" value={value} onChange={setValue} disabledDate={(d) => d.isBefore(moment(), "day")} />
                <Input.TextArea rows={2} maxLength={500} placeholder={tr().pause.note()} value={text} onChange={(e) => setText(e.target.value)} />
            </Space>
        </Modal>
    );
};

export default BookingDunningsPanel;
